import { ErrorHandler, Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { NotificationService } from '../services/notification.service';

/**
 * Global Error Handler
 * Catches all unhandled errors in the Angular application
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly notification = inject(NotificationService);

  handleError(error: Error | HttpErrorResponse): void {
    // Don't handle HTTP errors here - they're handled by the interceptor
    if (error instanceof HttpErrorResponse) {
      console.error('HTTP error caught by global handler (already handled):', error);
      return;
    }

    // Handle chunk load failures (happens when user has stale cache after a new deployment)
    if (this.isChunkLoadError(error)) {
      console.warn('Chunk load error detected, reloading page to fetch new assets.');
      window.location.reload();
      return;
    }

    // Handle frontend/JavaScript errors
    let errorMessage = 'An unexpected error occurred';

    if (error instanceof Error) {
      errorMessage = error.message || errorMessage;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    console.error('Frontend Error:', error);

    if (this.isDevelopment()) {
      this.notification.showError(`Error: ${errorMessage}`);
    } else {
      this.notification.showError('Something went wrong. Please try again.');
    }
  }

  private isChunkLoadError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return (
      message.includes('Loading chunk') ||
      message.includes('Importing a module script failed') ||
      message.includes('Failed to fetch dynamically imported module')
    );
  }

  private isDevelopment(): boolean {
    // Previous check (`!hostname.includes('production')`) returned true on
    // *every* deployment because none of our hostnames contain the literal
    // string "production" — so prod users were seeing raw English error
    // messages like "Error: No split option selected" via the dev branch
    // below. Read the production flag straight off the environment file
    // instead; it's set per build target in apps/tabify-app/project.json.
    return !environment.production;
  }
}
